import { apiService } from './apiService';
import axios from 'axios';
import { API_URL } from '../config';
import { ClarificationPayload, MessageCreate } from '../types/chat';
import { SubjectWithThreads } from '../types/subjects';
import { withCsrfHeader } from '../utils/authSession';

interface Chat {
    id: string;
    name: string;
}

interface MessageResponse {
    id: string;
    content: string;
    thread_id: string;
    message_type: string;
    subject_id: string;
    role: string;
    sequence_number: number;
    created_at: string;
    updated_at: string;
    media_id?: string;
    media_path?: string;
    thread_title: string;
}

interface UserResponse {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    grade: string;
    school_level: string;
    has_seen_subject_upsell?: boolean;
    subjects_with_threads: SubjectWithThreads[];
}

interface SendMessageResponse {
    id: string;
    thread_id: string;
    subject_id: string;
    content: string;
    message_type: string;
    role: 'user' | 'assistant';
    created_at: string;
}

// /chat/upload/media returns a *message record* (not just the media blob metadata)
interface MediaUploadResponse {
    id: string; // message id
    content: string | null;
    thread_id: string;
    subject_id: string;
    message_type: string; // AUDIO | IMAGE | VIDEO | DOCUMENT | FILE
    role: 'user' | 'assistant';
    sequence_number: number;
    created_at: string;
    updated_at: string;
    media_id: string;
    voice_duration?: number | null;
    thread_title?: string;
    ai_message_id?: string;
    clarification?: ClarificationPayload;
}

export const chatService = {
    sendMessage: async (messageData: MessageCreate, subject_id: string): Promise<SendMessageResponse> => {
        try {
            const response = await apiService.post<SendMessageResponse>(
                `chat/messages?subject_id=${subject_id}`, 
                messageData
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Preserve existing error behavior; avoid noisy console debug logs in production.
            }
            throw error;
        }
    },

    /**
     * Send a message and receive AI response via Server-Sent Events (streaming).
     * This provides real-time response display for better UX.
     */
    sendMessageStream: async (
        messageData: MessageCreate,
        subject_id: string,
        onChunk: (chunk: string) => void,
        onComplete: (data: {
            ai_message_id: string;
            thread_id: string;
            clarification?: ClarificationPayload;
            suggested_image_path?: string;
            suggested_image_description?: string;
        }) => void,
        onError: (error: string) => void,
        onInit?: (data: { user_message_id: string; thread_id: string; thread_title: string }) => void
    ): Promise<{ thread_id: string } | void> => {
        let realThreadId: string | null = null;
        
        try {
            const url = `${API_URL}/chat/messages/stream?subject_id=${encodeURIComponent(subject_id)}`;

            const response = await fetch(
                url,
                {
                    method: 'POST',
                    headers: withCsrfHeader({
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream',
                    }),
                    credentials: 'include',
                    body: JSON.stringify(messageData),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('No response body reader available');
            }

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                
                // Process complete SSE messages
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            
                            switch (data.type) {
                                case 'init':
                                    console.log('Stream initialized:', data);
                                    // Capture the real thread ID from backend
                                    realThreadId = data.thread_id;
                                    if (onInit) {
                                        onInit({
                                            user_message_id: data.user_message_id,
                                            thread_id: data.thread_id,
                                            thread_title: data.thread_title,
                                        });
                                    }
                                    break;
                                case 'chunk':
                                    console.log('Stream chunk received:', data.content?.substring(0, 50));
                                    onChunk(data.content);
                                    break;
                                case 'done':
                                    console.log('Stream done:', data);
                                    onComplete({
                                        ai_message_id: data.ai_message_id,
                                        thread_id: data.thread_id,
                                        clarification: data.clarification as ClarificationPayload | undefined,
                                        suggested_image_path: data.suggested_image_path,
                                        suggested_image_description: data.suggested_image_description,
                                    });
                                    break;
                                case 'error':
                                    console.error('Stream error event:', data.message);
                                    onError(data.message);
                                    break;
                            }
                        } catch (parseError) {
                            console.warn('Failed to parse SSE data:', line);
                        }
                    }
                }
            }
            
            // Return the real thread ID so callers can use it
            return realThreadId ? { thread_id: realThreadId } : undefined;
        } catch (error) {
            onError(error instanceof Error ? error.message : 'Unknown streaming error');
            throw error;
        }
    },

    archiveSubject: async (subject: string) => {
        return apiService.request({
            url: '/chat/subject/archive',
            method: 'POST',
            data: { subject_name: subject },
        });
    },

    subscribeSubject: async (subject: string) => {
        return apiService.request({
            url: `/subjects/${subject}/subscribe`,
            method: 'POST',
        });
    },

    unsubscribeSubject: async (subjectId: string) => {
        return apiService.request({
            url: `/subjects/${subjectId}/unsubscribe`,
            method: 'POST',
        });
    },

    getChats: async () => {
        return apiService.request<{ data: Chat[] }>({
            url: '/chat/list',
            method: 'GET',
        });
    },

    deleteChat: async (chatId: string) => {
        return apiService.request({
            url: `/chat/delete/${chatId}`,
            method: 'DELETE',
        });
    },

    deleteMessage: async (messageId: string): Promise<{ message: string; message_id: string }> => {
        const response = await apiService.request<{ message: string; message_id: string }>({
            url: `/chat/messages/${messageId}`,
            method: 'DELETE',
        });
        return response.data;
    },

    deleteAllChats: async () => {
        const response = await apiService.request({
            url: '/chat/all/chats',
            method: 'DELETE'
        });
        return response;
    },

    getMessagesBySubject: async (subjectId: string, lastMessageId?: string): Promise<MessageResponse> => {
        const response = await axios.get(`${API_URL}/chat/messages/${subjectId}`, {
            params: { lastMessageId }
        });
        return response.data as MessageResponse;
    },

    getUserSubjectsProfile: async (): Promise<UserResponse> => {
        console.log('Fetching user subjects profile...');
        try {
            const response = await apiService.get<UserResponse>('/users/subjects');
            console.log('Raw API response:', response);
            if (!response.data) {
                throw new Error('Missing user subjects response payload');
            }
            return response.data;
        } catch (error) {
            console.error('Error in getUserSubjectsProfile:', error);
            if (axios.isAxiosError(error)) {
                console.error('API Error Details:', {
                    status: error.response?.status,
                    data: error.response?.data,
                    config: error.config
                });
            }
            throw error;
        }
    },

    getUserSubjects: async (): Promise<SubjectWithThreads[]> => {
        const profile = await chatService.getUserSubjectsProfile();
        if (!Array.isArray(profile.subjects_with_threads)) {
            return [];
        }
        return profile.subjects_with_threads;
    },

    markSubjectUpsellSeen: async (): Promise<{ status: string; has_seen_subject_upsell: boolean }> => {
        const response = await apiService.post<{ status: string; has_seen_subject_upsell: boolean }>(
            '/users/subject-upsell/seen',
            {}
        );
        return response.data;
    },

    uploadMedia: async (
        file: Blob, 
        duration: number, 
        threadId: string, 
        subjectId: string
    ): Promise<MediaUploadResponse> => {
        try {
            const formData = new FormData();
            formData.append('file', file, 'voice-message.webm');
            formData.append('duration', Math.round(duration).toString());
            formData.append('file_type', 'AUDIO');
            formData.append('thread_id', threadId);
            formData.append('subject_id', subjectId);

            console.log('Uploading media with:', {
                duration: Math.round(duration),
                threadId,
                subjectId,
                fileType: file.type,
                fileSize: file.size
            });

            const response = await apiService.post<MediaUploadResponse>(
                '/chat/upload/media',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error in chatService.uploadMedia:', error);
            if (axios.isAxiosError(error)) {
                console.error('API Error Details:', {
                    status: error.response?.status,
                    data: error.response?.data,
                    config: error.config
                });
            }
            throw error;
        }
    },

    getMedia: async (mediaId: string): Promise<string> => {
        try {
            const response = await apiService.get<Blob>(
                `/chat/media/${mediaId}`,
                {
                    responseType: 'blob',
                }
            );
            return URL.createObjectURL(response.data);
        } catch (error) {
            console.error('Error in chatService.getMedia:', error);
            if (axios.isAxiosError(error)) {
                console.error('API Error Details:', {
                    status: error.response?.status,
                    data: error.response?.data,
                    config: error.config
                });
            }
            throw error;
        }
    },

    uploadFile: async (
        file: File,
        threadId: string,
        subjectId: string,
        message: string,
        contextType?: string
    ): Promise<MediaUploadResponse> => {
        try {
            // Determine file type and validation rules
            let fileType = 'FILE';
            let requiresContent = true;
            let requiresDuration = false;

            if (file.type.startsWith('image/')) {
                fileType = 'IMAGE';
                requiresContent = false;
            } else if (file.type.startsWith('video/')) {
                fileType = 'VIDEO';
                requiresContent = false;
                requiresDuration = true;
            } else if (file.type.startsWith('audio/')) {
                fileType = 'AUDIO';
                requiresContent = false;
                requiresDuration = true;
            } else if (file.type.includes('pdf') || 
                      file.type.includes('document') || 
                      file.type.includes('msword')) {
                fileType = 'DOCUMENT';
                requiresContent = true;
            }

            let resolvedMessage = String(message || '').trim();
            if (!resolvedMessage && fileType === 'DOCUMENT') {
                const fallbackName = String(file.name || '').trim();
                resolvedMessage = fallbackName || 'Uploaded document';
            }

            // Validate content based on file type
            if (requiresContent && !resolvedMessage) {
                throw new Error(`Please provide a description for your ${fileType.toLowerCase()}`);
            }

            const formData = new FormData();
            formData.append('file', file);
            formData.append('thread_id', threadId);
            formData.append('subject_id', subjectId);
            formData.append('file_type', fileType);
            if (contextType) {
                formData.append('context_type', contextType);
            }
            
            // Add content if provided or required
            if (resolvedMessage) {
                formData.append('content', resolvedMessage);
            }

            // Only add duration for audio/video
            if (requiresDuration) {
                formData.append('duration', '0');
            }

            console.log('Uploading file:', {
                type: fileType,
                requiresContent,
                requiresDuration,
                fileName: file.name,
                fileSize: file.size,
                content: resolvedMessage,
                formData: Object.fromEntries(formData.entries())
            });

            const response = await apiService.post<MediaUploadResponse>(
                '/chat/upload/media',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                console.error('Upload error details:', error.response?.data);
            }
            throw error;
        }
    },
};
